/**
 * Plan detail cache — module-level, survives React Router remounts.
 *
 * WHY: PlanDetail was refetching /api/plans/:id on every visit, causing
 * a 500-1500ms blank-page flash. This cache makes revisits and back-nav
 * feel INSTANT while still refreshing in the background (SWR).
 *
 * Also exposes a hover-prefetch primitive so marketplace cards can warm
 * the cache before the user clicks — turning cold-cache visits into
 * warm-cache visits nearly every time.
 *
 * Architecture is intentionally simple (Map + inFlight promise map) so
 * we don't take on React Query as a dependency for a single flow. If we
 * later need query invalidation or subscriptions, this file is the swap
 * point.
 */

import api from "./api";

const _plans = new Map();          // planId → { data, ts }
const _related = new Map();        // planId → { data, ts }
const _inFlight = new Map();       // planId → Promise (dedupes concurrent prefetch + hover + click)
const _relatedInFlight = new Map();

const PLAN_TTL_MS = 3 * 60 * 1000;    // 3 minutes — long enough for browsing, short enough to catch admin edits

export function getCachedPlan(planId) {
  const e = _plans.get(planId);
  return e?.data || null;
}

export function isPlanFresh(planId) {
  const e = _plans.get(planId);
  return !!(e && (Date.now() - e.ts) < PLAN_TTL_MS);
}

export function setCachedPlan(planId, data) {
  if (!planId || !data) return;
  _plans.set(planId, { data, ts: Date.now() });
}

export function getCachedRelated(planId) {
  const e = _related.get(planId);
  return e?.data || null;
}

export function setCachedRelated(planId, data) {
  if (!planId) return;
  _related.set(planId, { data: data || [], ts: Date.now() });
}

/**
 * Fetch a plan; if a fetch for this id is already in flight, return that
 * shared promise instead of firing a duplicate. Warms `_plans` on success.
 */
export function fetchPlan(planId) {
  if (!planId) return Promise.reject(new Error("no planId"));
  if (_inFlight.has(planId)) return _inFlight.get(planId);
  const p = api.get(`/plans/${planId}`)
    .then((r) => {
      setCachedPlan(planId, r.data);
      return r.data;
    })
    .finally(() => _inFlight.delete(planId));
  _inFlight.set(planId, p);
  return p;
}

export function fetchRelated(planId, limit = 4) {
  if (!planId) return Promise.resolve([]);
  if (_relatedInFlight.has(planId)) return _relatedInFlight.get(planId);
  const p = api.get(`/plans/${planId}/related`, { params: { limit } })
    .then((r) => {
      setCachedRelated(planId, r.data);
      return r.data;
    })
    .catch(() => [])
    .finally(() => _relatedInFlight.delete(planId));
  _relatedInFlight.set(planId, p);
  return p;
}

/**
 * Hover-prefetch: called from PlanCard when the mouse enters. Safe to
 * spam — dedup via _inFlight. Also preloads the hero image variant so
 * clicking through is truly instant.
 */
export function prefetchPlan(planId, heroUrl) {
  if (isPlanFresh(planId)) return;
  fetchPlan(planId).catch(() => {});
  if (heroUrl) {
    // Warm the browser's HTTP cache for the hero image.
    const img = new Image();
    img.src = heroUrl;
  }
}

// Expose for debug / testing.
if (typeof window !== "undefined") {
  window.__dhePlanCache = { _plans, _related, _inFlight };
}
